import React, { useState, useRef, useEffect } from "react";
import {
  Typography,
  Container,
  Box,
  Tab,
  Paper,
  Tabs,
  Popper,
  useMediaQuery,
  useTheme,
  IconButton,
  Tooltip,
} from "@mui/material";
import SupportAgentIcon from "@mui/icons-material/SupportAgent";
import ChatIcon from "@mui/icons-material/Chat";
import ContactPhoneIcon from "@mui/icons-material/ContactPhone";
import { useNavigate } from "react-router-dom";
import HomeFooter from "./HomeFooter";
import Contactus from "./Contact";
import { motion } from "framer-motion";
import Navbar from "./Navbar";
import "react-toastify/dist/ReactToastify.css";
import SchoolIcon from "@mui/icons-material/School";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import AssignmentIcon from "@mui/icons-material/Assignment";
import PsychologyIcon from "@mui/icons-material/Psychology";
import ChildCareIcon from "@mui/icons-material/ChildCare";

// Base URL of the Impulz website (move to env variable for production)
const IMPULZ_BASE =
  process.env.REACT_APP_IMPULZ_URL || "https://9ggv8r3x-3000.inc1.devtunnels.ms";

const HomePage = () => {
  const [expandedIndex, setExpandedIndex] = useState(null);

  // === DIALOG STATE ===
  const [contactOpen, setContactOpen] = useState(false);

  const navigate = useNavigate();
  const contactRef = useRef(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  useEffect(() => {
    const handleClickAnywhere = () => {
      if (expandedIndex !== null) {
        setExpandedIndex(null);
      }
    };
    document.addEventListener("click", handleClickAnywhere);
    return () => {
      document.removeEventListener("click", handleClickAnywhere);
    };
  }, [expandedIndex]);

  const scop = [
    {
      title: "Workforce Excellence",
      summary: "Empower teams with smarter people management.",
      image: "workforce-excellence.webp",
      points: [
        "Simplify employee lifecycle management from onboarding to exit.",
        "Automate attendance, payroll and leave workflows with accuracy.",
        "Enhance productivity through performance tracking and feedback.",
      ],
    },
    {
      title: "Customer Engagement",
      summary: "Strengthen relationships through intelligent interactions.",
      image: "customer-engagement.png",
      points: [
        "Centralize customer information for better communication.",
        "Track interactions, follow-ups and engagement history seamlessly.",
        "Drive customer satisfaction with timely and personalized responses.",
      ],
    },
    {
      title: "Business Offers",
      summary: "Manage services and offerings with complete clarity.",
      image: "business-offerings.jpg",
      points: [
        "Organize and maintain all offerings in a structured system.",
        "Track pricing, updates and availability in real time.",
        "Ensure consistency across sales, operations and delivery teams.",
      ],
    },
    {
      title: "Growth Opportunities",
      summary: "Convert prospects into long-term success.",
      image: "growth-opportunities.jpg",
      points: [
        "Capture and manage potential opportunities efficiently.",
        "Monitor progress through every stage of the growth cycle.",
        "Increase conversion rates with data-driven decision making.",
      ],
    },
    {
      title: "Time & Productivity",
      summary: "Optimize work hours with smart tracking.",
      image: "time-productivity.png",
      points: [
        "Track work hours, tasks and productivity effortlessly.",
        "Ensure accurate reporting for billing and internal analysis.",
        "Improve accountability with transparent time records.",
      ],
    },
    {
      title: "Talent Acquisition",
      summary: "Hire the right talent with confidence.",
      image: "talent-acquisition.png",
      points: [
        "Manage hiring workflows from job posting to onboarding.",
        "Track applicants, interviews and selection stages.",
        "Build strong teams with structured and efficient hiring.",
      ],
    },
  ];

  const [activeTab, setActiveTab] = useState(0);
  const handleChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const contentVariants = {
    hidden: { opacity: 0, x: 50 },
    visible: { opacity: 1, x: 0, transition: { duration: 0.5 } },
  };

  const horizontalProducts = [
    {
      id: 4,
      name: "Impulz",
      tagline: "Smarter Learning for the Digital Age",
      image: "/images/impulz_logo.png",
      color: "#9C27B0",
      externalUrl: `${IMPULZ_BASE}/`,
      // Same URL pattern as Footer: /product/<slug>
      children: [
        {
          text: "ImpulZ School LMS",
          path: "/product/school-lms",
          icon: <SchoolIcon />,
        },
        {
          text: "ImpulZ College LMS",
          path: "/product/college-lms",
          icon: <AccountBalanceIcon />,
        },
        {
          text: "ImpulZ TNPSC",
          path: "/product/tnpsc",
          icon: <AssignmentIcon />,
        },
        {
          text: "ImpulZ JEE / NEET",
          path: "/product/jee-neet",
          icon: <PsychologyIcon />,
        },
        {
          text: "ImpulZ Kids",
          path: "/product/kids",
          icon: <ChildCareIcon />,
        },
      ],
    },
    {
      id: 3,
      name: "Lider Connect",
      tagline: "Connect Systems. Simplify Workflows.",
      image: "/images/connect.png",
      color: "#FF9800",
      onClick: () => navigate("/products/lider-connect"),
    },
    {
      id: 2,
      name: "Lider ERP",
      tagline: "All-in-One Business Management",
      image: "/images/cloud_new.png",
      color: "#4CAF50",
      onClick: () => navigate("/products/lider-erp"),
    },
    {
      id: 10,
      name: "Koottu",
      tagline: "Digital Credit Made Simple",
      image: "/images/koottu.png",
      color: "#1a237e",
      onClick: () => navigate("/products/bower-cred"),
    },
    {
      id: 6,
      name: "Circuit",
      tagline: "Powering Connected Experiences",
      image: "/images/circuit_logo.png",
      color: "#FF5722",
      onClick: () => navigate("/products/circuit"),
    },
    {
      id: 5,
      name: "Lider SecureGate",
      tagline: "Smart Technology. Smarter Decisions.",
      image: "/images/SecureGate.png",
      color: "#F44336",
      onClick: () => navigate("/products/iclever-gate"),
    },
    {
      id: 8,
      name: "iPathway",
      tagline: "Your Roadmap to Digital Success",
      image: "/images/p1.png",
      color: "#3F51B5",
      onClick: () => navigate("/products/pathway"),
    },
    {
      id: 1,
      name: "Missed Call Alert",
      tagline: "Never Miss an Important Call",
      image: "/images/miss.png",
      onClick: () => navigate("/products/missed-call-alert"),
    },
    {
      id: 9,
      name: "Thedal Directory",
      tagline: "Insights That Drive Growth",
      image: "/images/Thedal.png",
      color: "#795548",
      onClick: () => navigate("/products/thedal-dictionary"),
    },
    {
      id: 7,
      name: "Kiosk",
      tagline: "Interactive Self-Service Made Simple",
      image: "/images/kisok_logo.png",
      color: "#009688",
      onClick: () => navigate("/products/kiosk"),
    },
  ];

  const colors = {
    primary: "#1976d2",
    primaryDark: "#0d47a1",
    primaryLight: "#64b5f6",
    secondary: "#e3f2fd",
    backgroundLight: "#f5f9ff",
    textDark: "#1e3a8a",
    textLight: "#ffffff",
    accent: "#2196f3",
    success: "#4caf50",
    paper: "#ffffff",
  };
  const bgImage = `${process.env.PUBLIC_URL}/images/lider_copy2.png`;

  const sectionHeadingSx = {
    fontWeight: 900,
    textAlign: "center",
    mb: 8,
    color: colors.textDark,
    fontFamily: "'Marquis', serif",
    fontSize: { xs: "1.75rem", md: "2.5rem" },
    textTransform: "uppercase",
    letterSpacing: 1,
    position: "relative",
    "&::after": {
      content: '""',
      position: "absolute",
      bottom: -10,
      left: "50%",
      transform: "translateX(-50%)",
      width: 80,
      height: 4,
      backgroundColor: colors.primary,
      borderRadius: 2,
    },
  };

  // === 3 SUPPORT ITEMS ===
  const supportItems = [
    {
      icon: (
        <ContactPhoneIcon sx={{ fontSize: 40, color: colors.primary }} />
      ),
      title: "Contact Sales Team",
      desc: "Talk directly with our sales team to find the perfect solutions tailored for your business.",
    },
    {
      icon: (
        <SupportAgentIcon sx={{ fontSize: 40, color: colors.primary }} />
      ),
      title: "Support",
      desc: "Our 24/7 support portal lets you find answers quickly or connect with specialists for expert assistance.",
    },
    {
      icon: <ChatIcon sx={{ fontSize: 40, color: colors.primary }} />,
      title: "Chat With Our Product Team",
      desc: "Get personalized, industry-specific insights and discover the right solutions to elevate your business.",
    },
  ];

  return (
    <Box
      sx={{
        width: "100%",
        maxWidth: "100vw",
        // ✅ Removed mt: 3 — was causing extra gap on mobile
      }}
    >
      <Navbar contactRef={contactRef} />

      {/* ================= HERO SECTION ================= */}
      <Box
        sx={{
          position: "relative",
          width: "100%",

          // ✅ Mobile: flush with navbar (no gap)
          // ✅ Desktop: small top gap
          mt: { xs: "70px", md: "10px" },

          overflow: "hidden",
          borderRadius: { xs: "8px", md: "20px" },
          border: "1px solid rgba(255,255,255,0.15)",
          boxShadow: `
            0 24px 80px rgba(0,0,0,0.2),
            inset 0 1px 0 rgba(255,255,255,0.1)
          `,
          lineHeight: 0, // ✅ removes inline img bottom gap
        }}
      >
        {/* ✅ Full-width hero image — edge to edge, no side gaps, no crop */}
        <Box
          component="img"
          src={bgImage}
          alt="Lider Technology Solutions"
          sx={{
            display: "block",
            width: "100%",
            height: "auto", // ✅ auto height preserves aspect ratio (no crop)
            userSelect: "none",
            pointerEvents: "none",
          }}
        />

        {/* Decorative overlay (grid + gif) */}
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            backgroundImage: `
              linear-gradient(rgba(255,255,255,0.03) 1px, transparent 1px),
              linear-gradient(90deg, rgba(255,255,255,0.03) 1px, transparent 1px),
              url('/images/hero-bg.gif')
            `,
            backgroundSize: "50px 50px, 50px 50px, cover",
            backgroundPosition: "center",
            backgroundRepeat: "repeat, repeat, no-repeat",
            opacity: 0.35,
            zIndex: 1,
            pointerEvents: "none",
          }}
        />
      </Box>

      {/* ================= OUR CAPABILITIES ================= */}
      <Box sx={{ background: colors.backgroundLight, py: 6, width: "100%" }}>
        <Container maxWidth={false} sx={{ px: { xs: 2, md: 6 } }}>
          <Typography variant="h3" sx={sectionHeadingSx}>
            OUR CAPABILITIES
          </Typography>

          <Tabs
  value={activeTab}
  onChange={handleChange}
  variant="scrollable"
  scrollButtons="auto"
  allowScrollButtonsMobile
  textColor="primary"
  indicatorColor="primary"
  sx={{
    mb: 5,
    width: "100%",

    // ✅ Desktop only: center the tabs
    "& .MuiTabs-scroller": {
      display: { md: "flex" },
      justifyContent: { md: "center" },
    },
    "& .MuiTabs-flexContainer": {
      gap: { xs: 1, md: 2 },
      justifyContent: { xs: "flex-start", md: "center" },
      width: { md: "100%" },
    },

    "& .MuiTab-root": {
      fontWeight: "bold",
      fontFamily: "Marquis",
      fontSize: { xs: "0.85rem", md: "1.2rem" },
      textTransform: "capitalize",
      px: { xs: 2, md: 4 },
      py: 1.5,
      borderRadius: 3,
      minWidth: { xs: "auto", md: 120 },
      minHeight: { xs: 48, md: 56 },
      whiteSpace: "nowrap",
      transition: "all 0.3s ease",
      color: "#555",
      backgroundColor: "transparent",
      "&:hover": {
        color: colors.primary,
        backgroundColor: "rgba(25, 118, 210, 0.1)",
      },
    },
    "& .Mui-selected": {
      color: `${colors.textLight} !important`,
      backgroundColor: colors.primary,
      boxShadow: "0 4px 10px rgba(25, 118, 210, 0.4)",
      "&:hover": {
        backgroundColor: colors.primaryDark,
      },
    },
    "& .MuiTabs-indicator": {
      height: 4,
      borderRadius: 2,
      backgroundColor: colors.primary,
      marginBottom: -4,
      transition: "all 0.3s ease",
    },
    "& .MuiTabs-scrollButtons": {
      color: colors.primary,
      "&.Mui-disabled": {
        opacity: 0.3,
      },
    },
  }}
>
  {scop.map((item, index) => (
    <Tab key={index} label={item.title} />
  ))}
          </Tabs>

          <motion.div
            key={activeTab}
            variants={contentVariants}
            initial="hidden"
            animate="visible"
          >
            <Paper
              elevation={3}
              sx={{
                p: { xs: 5, md: 8 },
                borderRadius: 4,
                background: colors.paper,
                display: "flex",
                flexDirection: { xs: "column", md: "row" },
                alignItems: "center",
                gap: 6,
                boxShadow: "0 10px 30px rgba(0,0,0,0.1)",
              }}
            >
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography
                  variant="h4"
                  sx={{
                    fontWeight: "bold",
                    color: colors.primary,
                    fontFamily: "Marquis",
                    mb: 1,
                  }}
                >
                  {scop[activeTab].title}
                </Typography>
                <Typography
                  variant="h6"
                  sx={{
                    fontFamily: "Marquis",
                    fontSize: { xs: "1.25rem", md: "1.5rem" },
                    color: "#555",
                    mb: 3,
                  }}
                >
                  {scop[activeTab].summary}
                </Typography>
                <Box
                  component="ul"
                  sx={{
                    pl: 4,
                    color: "#444",
                    fontSize: { xs: "1.1rem", md: "1.25rem" },
                  }}
                >
                  {scop[activeTab].points.map((point, idx) => (
                    <li key={idx} style={{ marginBottom: 12 }}>
                      {point}
                    </li>
                  ))}
                </Box>
              </Box>

              <Box
                sx={{
                  flex: 1,
                  width: "100%",
                  minWidth: 0,
                  height: { xs: 250, md: 420 },
                  borderRadius: 3,
                  overflow: "hidden",
                  boxShadow: "0 8px 20px rgba(0,0,0,0.15)",
                }}
                component={motion.div}
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.6 }}
              >
                <img
                  src={`/images/${scop[activeTab].image}`}
                  alt={scop[activeTab].title}
                  style={{
                    width: "100%",
                    height: "100%",
                    objectFit: "fill",
                    borderRadius: 12,
                    display: "block",
                  }}
                />
              </Box>
            </Paper>
          </motion.div>
        </Container>
      </Box>

      {/* ================= SUPPORT SECTION ================= */}
      <Box
        sx={{
          maxWidth: "100%",
          mx: "auto",
          p: { xs: 4, md: 8 },
          backgroundColor: colors.secondary,
          borderRadius: 3,
          boxShadow: `0 12px 35px ${colors.primaryLight}33`,
        }}
      >
        <Typography variant="h3" sx={sectionHeadingSx}>
          How Can We Help?
        </Typography>

        <Typography
          variant="h6"
          sx={{
            maxWidth: 700,
            mx: "auto",
            color: "#424242",
            fontWeight: "500",
            textAlign: "center",
            mb: 6,
            fontSize: { xs: "1rem", md: "1.25rem" },
            lineHeight: 1.6,
          }}
        >
          Empowering your business with smart solutions tailored to drive
          growth, boost efficiency and lead the future of your industry.
        </Typography>

        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            justifyContent: "space-around",
            flexWrap: "wrap",
            gap: 4,
            px: 2,
          }}
        >
          {supportItems.map((item, idx) => (
            <Box
              key={idx}
              component={motion.div}
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: idx * 0.2 }}
              viewport={{ once: true }}
              onClick={() => setContactOpen(true)}
              sx={{
                width: 300,
                maxWidth: "100%",
                textAlign: "center",
                cursor: "pointer",
                transition: "transform 0.2s ease",
                "&:hover": {
                  transform: "translateY(-6px)",
                },
              }}
            >
              {item.icon}
              <Typography
                variant="h6"
                sx={{ fontWeight: 600, mt: 1, color: colors.primaryDark }}
              >
                {item.title}
              </Typography>
              <Typography
                sx={{ color: "#555", fontSize: "0.95rem", mt: 1, mb: 2 }}
              >
                {item.desc}
              </Typography>
            </Box>
          ))}
        </Box>
      </Box>
      <br />

      {/* ================= PRODUCTS SECTION ================= */}
      <Typography variant="h3" sx={sectionHeadingSx}>
        Our Products
      </Typography>

      <InfiniteLogoCarousel
        horizontalProducts={horizontalProducts}
        colors={colors}
      />

      {/* === DIALOG === */}
      <Contactus open={contactOpen} onClose={() => setContactOpen(false)} />

      {/* === FLOATING CHAT BUTTON === */}
      <Tooltip title="Chat with us" placement="left" arrow>
        <IconButton
          onClick={() => setContactOpen(true)}
          sx={{
            position: "fixed",
            bottom: { xs: 20, md: 32 },
            right: { xs: 20, md: 32 },
            width: { xs: 60, md: 70 },
            height: { xs: 60, md: 70 },
            zIndex: 1200,
            background:
              "linear-gradient(135deg, #4fc3f7 0%, #29b6f6 100%)",
            color: "#ffffff",
            boxShadow: "0 8px 24px rgba(33,150,243,0.45)",
            border: "4px solid rgba(255,255,255,0.9)",
            "&:hover": {
              background:
                "linear-gradient(135deg, #29b6f6 0%, #039be5 100%)",
              transform: "scale(1.08)",
            },
            transition: "all 0.25s ease",
            "&::before": {
              content: '""',
              position: "absolute",
              inset: -8,
              borderRadius: "50%",
              border: "2px solid rgba(79,195,247,0.5)",
              animation: "pulseRing 2s infinite",
            },
            "@keyframes pulseRing": {
              "0%": {
                transform: "scale(0.9)",
                opacity: 0.9,
              },
              "70%": {
                transform: "scale(1.3)",
                opacity: 0,
              },
              "100%": {
                transform: "scale(1.3)",
                opacity: 0,
              },
            },
          }}
        >
          <ChatIcon sx={{ fontSize: { xs: 28, md: 34 } }} />
        </IconButton>
      </Tooltip>

      <HomeFooter />
    </Box>
  );
};

// =============================================================
// INFINITE LOGO CAROUSEL
// =============================================================
const InfiniteLogoCarousel = ({ horizontalProducts, colors }) => {
  const [isPaused, setIsPaused] = useState(false);
  const [duplicatedProducts, setDuplicatedProducts] = useState([]);
  const [hoveredProduct, setHoveredProduct] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);

  const scrollRef = useRef(null);
  const animationRef = useRef(null);
  const closeTimerRef = useRef(null);

  const navigate = useNavigate();

  useEffect(() => {
    if (horizontalProducts?.length > 0) {
      setDuplicatedProducts([
        ...horizontalProducts,
        ...horizontalProducts,
        ...horizontalProducts,
      ]);
    }
  }, [horizontalProducts]);

  useEffect(() => {
    const scrollContainer = scrollRef.current;

    if (!scrollContainer || duplicatedProducts.length === 0) {
      return;
    }

    let startTime = null;
    const scrollSpeed = 0.04;

    const animate = (timestamp) => {
      if (!startTime) {
        startTime = timestamp;
      }

      if (!isPaused && !hoveredProduct) {
        const elapsed = timestamp - startTime;
        const scrollAmount = elapsed * scrollSpeed;

        if (scrollContainer.scrollLeft >= scrollContainer.scrollWidth / 3) {
          scrollContainer.scrollLeft = 0;
          startTime = timestamp;
        } else {
          scrollContainer.scrollLeft += scrollAmount;
          startTime = timestamp;
        }
      } else {
        startTime = timestamp;
      }

      animationRef.current = requestAnimationFrame(animate);
    };

    animationRef.current = requestAnimationFrame(animate);

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [isPaused, hoveredProduct, duplicatedProducts.length]);

  // cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
    };
  }, []);

  // =========================================================
  // HOVER HANDLERS (same idea as Footer)
  // =========================================================

  const cancelClose = () => {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  };

  const scheduleClose = () => {
    cancelClose();
    // small delay so the mouse can travel from logo -> submenu
    closeTimerRef.current = setTimeout(() => {
      setHoveredProduct(null);
      setAnchorEl(null);
      setIsPaused(false);
    }, 150);
  };

  const handleProductMouseEnter = (event, product) => {
    setIsPaused(true);
    cancelClose();

    if (product.children && product.children.length > 0) {
      setAnchorEl(event.currentTarget);
      setHoveredProduct(product);
    } else {
      setHoveredProduct(null);
      setAnchorEl(null);
    }
  };

  const handleProductMouseLeave = () => {
    scheduleClose();
  };

  // =========================================================
  // SUBMENU ITEM CLICK (same tab navigation, like Footer)
  // =========================================================

  const openChild = (child) => {
    window.open(`${IMPULZ_BASE}${child.path}`, "_blank", "noopener,noreferrer");
  };

  const logoScale = (name) => {
    switch (name) {
      case "Lider ERP":
        return "scale(1.00)";
      case "Circuit":
        return "scale(1.25)";
      case "Lider SecureGate":
        return "scale(1.15)";
      case "iPathway":
        return "scale(1.35)";
      case "Missed Call Alert":
        return "scale(1.35)";
      case "Impulz":
        return "scale(1.20)";
      case "Lider Connect":
        return "scale(1.05)";
      case "Thedal Directory":
        return "scale(1.05)";
      case "Kiosk":
        return "scale(1.25)";
      case "Koottu":
        return "scale(1.20)";
      default:
        return "scale(1.2)";
    }
  };

  const submenuOpen = Boolean(hoveredProduct && anchorEl);

  return (
    <Box
      sx={{
        width: "100%",
        bgcolor: "#f8fafc",
        py: { xs: 4, md: 5 },
        px: { xs: 1, md: 8 },
        mb: 8,
        overflow: "hidden",
        position: "relative",
      }}
      onMouseEnter={() => {
        if (!hoveredProduct) {
          setIsPaused(false);
        }
      }}
      onMouseLeave={() => {
        if (!hoveredProduct) {
          setIsPaused(false);
        }
      }}
    >
      <Box
        sx={{
          position: "absolute",
          left: 0,
          top: 0,
          bottom: 0,
          width: { xs: 30, md: 70 },
          background: "linear-gradient(90deg, #f8fafc 0%, transparent 100%)",
          zIndex: 2,
          pointerEvents: "none",
        }}
      />

      <Box
        sx={{
          position: "absolute",
          right: 0,
          top: 0,
          bottom: 0,
          width: { xs: 30, md: 70 },
          background: "linear-gradient(-90deg, #f8fafc 0%, transparent 100%)",
          zIndex: 2,
          pointerEvents: "none",
        }}
      />

      <Box
        ref={scrollRef}
        sx={{
          display: "flex",
          alignItems: "flex-start",
          gap: { xs: 2, sm: 4, md: 7 },
          overflowX: "auto",
          overflowY: "visible",
          scrollbarWidth: "none",
          "&::-webkit-scrollbar": {
            display: "none",
          },
          msOverflowStyle: "none",
        }}
      >
        {duplicatedProducts.map((product, index) => (
          <Box
            key={`${product.id}-${index}`}
            onMouseEnter={(event) => handleProductMouseEnter(event, product)}
            onMouseLeave={handleProductMouseLeave}
            sx={{
              minWidth: "180px",
              maxWidth: "180px",
              flexShrink: 0,
              position: "relative",
            }}
          >
            <motion.div
              whileHover={{
                scale: 1.05,
                transition: { duration: 0.2 },
              }}
              onClick={() => {
                if (product.externalUrl) {
                  // Same tab navigation (same as Footer)
                  window.location.href = product.externalUrl;
                } else if (product.onClick) {
                  product.onClick();
                } else {
                  navigate(
                    `/products/${product.name
                      .toLowerCase()
                      .replace(/\s+/g, "-")}`
                  );
                }
              }}
              style={{ cursor: "pointer" }}
            >
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "flex-start",
                  height: "180px",
                }}
              >
                <Box
                  sx={{
                    width: { xs: 100, sm: 110, md: 115 },
                    height: { xs: 100, sm: 110, md: 115 },
                    borderRadius: "50%",
                    border: "2px solid",
                    borderColor: product.color || colors.primary,
                    backgroundColor: "#fff",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    overflow: "hidden",
                    flexShrink: 0,
                    boxShadow: "0 3px 10px rgba(0,0,0,0.08)",
                  }}
                >
                  <Box
                    component="img"
                    src={product.image}
                    alt={product.name}
                    sx={{
                      width: "100%",
                      height: "100%",
                      objectFit: "contain",
                      transform: logoScale(product.name),
                      transition: "transform 0.3s ease",
                      display: "block",
                    }}
                    onError={(e) => {
                      e.target.src = "/images/placeholder.png";
                    }}
                  />
                </Box>

                <Typography
                  variant="subtitle1"
                  sx={{
                    mt: 1.8,
                    fontWeight: 600,
                    color: colors.textDark,
                    textAlign: "center",
                    fontSize: {
                      xs: "0.85rem",
                      sm: "0.95rem",
                      md: "1rem",
                    },
                    lineHeight: 1.3,
                    minHeight: "25px",
                    whiteSpace: "nowrap",
                  }}
                >
                  {product.name}
                </Typography>
              </Box>
            </motion.div>
          </Box>
        ))}
      </Box>

      {/* =====================================================
          IMPULZ HOVER SUBMENU (Popper = portal, no clipping)
      ===================================================== */}
      <Popper
        open={submenuOpen}
        anchorEl={anchorEl}
        placement="bottom"
        style={{ zIndex: 9999 }}
        modifiers={[
          { name: "offset", options: { offset: [0, -50] } },
          { name: "flip", enabled: true },
          { name: "preventOverflow", enabled: true, options: { padding: 8 } },
        ]}
      >
        <Paper
          onMouseEnter={cancelClose}
          onMouseLeave={scheduleClose}
          elevation={0}
          sx={{
            width: 285,
            background: "#ffffff",
            borderRadius: "10px",
            boxShadow: "0 10px 35px rgba(0,0,0,0.30)",
            border: "1px solid rgba(0,0,0,0.08)",
            p: 1,
          }}
        >
          {hoveredProduct?.children?.map((child) => (
            <Box
              key={child.text}
              onClick={() => openChild(child)}
              sx={{
                display: "flex",
                alignItems: "center",
                px: 1.5,
                py: 1.4,
                borderRadius: "8px",
                cursor: "pointer",
                transition: "all 0.2s ease",
                "&:hover": {
                  backgroundColor: "rgba(37, 99, 235, 0.08)",
                },
              }}
            >
              <Box
                sx={{
                  width: 38,
                  height: 38,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "#2563eb",
                  flexShrink: 0,
                  mr: 1.5,
                }}
              >
                {child.icon}
              </Box>

              <Typography
                sx={{
                  color: "#374151",
                  fontSize: "14px",
                  fontWeight: 500,
                  whiteSpace: "nowrap",
                }}
              >
                {child.text}
              </Typography>
            </Box>
          ))}
        </Paper>
      </Popper>
    </Box>
  );
};

export default HomePage;
